// Strategy-owned classifier for why a month isn't paying. Centralizes the
// rule so all strategies emit the same taxonomy and the UI never has to
// re-derive it. Mirrors the day-classification order in `cmTwoStrategy`
// (and any future strategy that uses 2-ADL gating + a waiting period):
//   1. <2 ADL bucket consumes first
//   2. WP days are consumed next
//   3. If the remainder still isn't paying and care is in Independent
//      Living, surface 'il-not-covered' (the reimbursement carve-out: IL is
//      not a qualified care setting for these policies)
//   4. Anything remaining is eligible
//
// Returns null when the month is paying (or could pay — the month's payout
// fields are the source of truth for that).

import { CareEnvironment } from '../../../appModel';
import { getWaitingPeriodBucketForEnvironment } from './sharedPolicyConfig';

export const EMPTY_INELIGIBLE_DAY_BREAKDOWN: IneligibleDayBreakdown = {
  lt2AdlDays: 0,
  inWpDays: 0,
  ilNotCoveredDays: 0,
  prePolicyDays: 0,
};

export interface DeriveIneligibilityReasonInput<TState = unknown> {
  careEnvironment: CareEnvironment;
  facts: MonthlyCareFacts;
  openState: MonthlyState<TState>;
  closeState: MonthlyState<TState>;
  payout: PayoutSummary;
}

export function deriveIneligibilityReason<TState>(
  input: DeriveIneligibilityReasonInput<TState>,
): ProjectionIneligibilityReason | null {
  const { careEnvironment, facts, openState, closeState, payout } = input;

  // A month that paid (regular indemnity or cost coverage) is not
  // ineligible. The EP lump month is a special case: it satisfies the WP
  // and pays retroactively, so we still classify it as `in-wp` because
  // the month itself didn't earn ongoing benefits — it reimbursed prior
  // ineligible months. Defer to the WP check below for that case.
  const paid = payout.indemnityPayout > 0 || payout.ltcCoveragePaid > 0;
  if (paid) return null;

  if (facts.non2AdlDays > 0) return 'lt-2-adl';

  const wpBucket = getWaitingPeriodBucketForEnvironment(careEnvironment);
  const openRelevantWp =
    wpBucket === 'homeDays'
      ? openState.waitingPeriod.homeDays
      : openState.waitingPeriod.facilityDays;
  const closeRelevantWp =
    wpBucket === 'homeDays'
      ? closeState.waitingPeriod.homeDays
      : closeState.waitingPeriod.facilityDays;
  const wpOpen = openRelevantWp > 0;
  const wpClose = closeRelevantWp > 0;
  if (wpOpen || wpClose) return 'in-wp';

  // IL is not a qualified care setting on reimbursement policies (see
  // `getDailyBenefitAmountForEnvironment` carve-out). When the 2-ADL gate
  // and WP have both passed but the month still didn't pay because care is
  // happening in IL, surface that as the reason rather than leaving it
  // unclassified.
  if (careEnvironment === CareEnvironment.independentLiving) {
    return 'il-not-covered';
  }

  return null;
}

// Companion to `deriveIneligibilityReason`. The reason field collapses a row
// to a single primary label; this returns the underlying day-by-day allocation
// so the UI can stack reasons within a segment (e.g. a transition month that
// contributed 15 days to the 2-ADL bucket and 15 days to WP shows both, not
// just the primary). Derived purely from facts + careEnvironment + payout so
// every strategy that already populates the standard facts gets the right
// answer without per-strategy plumbing.
//
// Day taxonomy (mutually exclusive within a row):
//   - lt2AdlDays:      from facts.non2AdlDays (the < 2 ADL bucket at phase head)
//   - inWpDays:        the active-environment bucket's consumed WP days this row
//   - ilNotCoveredDays: post-2-ADL/post-WP eligible days in Independent Living
//                       on a non-paying row (the reimbursement carve-out)
//
// Sum is ≤ DAYS_IN_MONTH; the remainder is paying days (or stranded eligible
// days on products that can't pay a partial month, e.g. CM II indemnity).
export function deriveIneligibleDayBreakdown<TState>(
  input: DeriveIneligibilityReasonInput<TState>,
): IneligibleDayBreakdown {
  const { careEnvironment, facts, payout } = input;
  const lt2AdlDays = facts.non2AdlDays;
  const wpBucket = getWaitingPeriodBucketForEnvironment(careEnvironment);
  const inWpDays =
    wpBucket === 'homeDays'
      ? facts.waitingPeriodDaysConsumedHome
      : facts.waitingPeriodDaysConsumedFacility;
  const paid = payout.indemnityPayout > 0 || payout.ltcCoveragePaid > 0;
  const ilNotCoveredDays =
    careEnvironment === CareEnvironment.independentLiving && !paid
      ? facts.coverageEligibleDays
      : 0;
  // Pre-policy days are classified upstream (the strategy's pre-policy gate
  // emits a uniform breakdown via `buildPrePolicyMonth`), so an in-force row
  // routed through here never has any.
  return { lt2AdlDays, inWpDays, ilNotCoveredDays, prePolicyDays: 0 };
}
